import loginScreenImageUrl from "@/assets/login-screen.svg"
import { Button } from "@/components/chadcn/Button"
import { Checkbox } from "@/components/chadcn/Checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/chadcn/Dialog"
import { Label } from "@/components/chadcn/Label"
import CopyRightsFooter from "@/components/CopyRightsFooter"
import HeroImage from "@/components/HeroImage"
import { PasswordInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import TermsAndConditionsDrawer from "@/components/TermsAndConditionsDrawer"
import { useCheckSignUpInvitationQuery, useSignUpMutation } from "@/data/queries"
import { useDeviceCodeInfo } from "@/data/store"
import { backendErrorCodeFor } from "@/model/BackendErrors"
import { ROUTES } from "@/routes"
import { signUpSchema, type SignUpFormData } from "@/schemas/auth"
import PostLoginRedirect from "@/screens/auth/PostLoginRedirect"
import { zodResolver } from "@hookform/resolvers/zod"
import { Instant } from "@js-joda/core"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { generatePath, useNavigate, useSearchParams } from "react-router"

const SignUpPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token") ?? ""
  const userId = searchParams.get("entityId") ?? ""

  const [signUpSucceeded, setSignUpSucceeded] = useState(false)
  const [isTermsDrawerOpen, setIsTermsDrawerOpen] = useState(false)
  const [deviceCodeInfo, setDeviceCodeInfo] = useDeviceCodeInfo()

  useEffect(() => {
    if (!token || !userId) {
      navigate(ROUTES.SIGN_IN, { replace: true })
    }
  }, [token, userId, navigate])

  const invitationParams = token && userId ? { token, userId } : null

  const { isPending: isCheckingInvitation, error: checkInvitationError } =
    useCheckSignUpInvitationQuery(invitationParams)

  const { mutateAsync: signUp, isPending: isSigningUp, error: signUpError } = useSignUpMutation()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignUpFormData>({
    mode: "all",
    defaultValues: {
      password: "",
      passwordConfirmation: "",
      agreeToTerms: false as unknown as true,
      agreeToTextMessages: false as unknown as true,
    },
    resolver: zodResolver(signUpSchema),
  })

  const checkErrorCode = backendErrorCodeFor(checkInvitationError)
  const hasValidInvitation = !isCheckingInvitation && checkInvitationError === null

  const signUpErrorCode = backendErrorCodeFor(signUpError)
  const signUpErrorMessage =
    signUpErrorCode === "SIGN_UP_INVITATION_ALREADY_ACCEPTED"
      ? "This invitation has already been accepted. Please sign in."
      : signUpErrorCode === "WEAK_PASSWORD"
        ? "The password is commonly used and easily guessed. Please choose a stronger password."
        : signUpErrorCode
          ? "Something went wrong. Please try again."
          : null

  const invitationErrorMessage =
    checkErrorCode === "SIGN_UP_INVITATION_EXPIRED"
      ? "Your verification link has been expired. We've sent you a new invitation email."
      : checkErrorCode === "SIGN_UP_INVITATION_EXPIRED_SCAN_QR_CODE"
        ? "The invitation has been expired, please scan the QR code again."
        : checkErrorCode === "SIGN_UP_INVITATION_ALREADY_ACCEPTED"
          ? "This invitation has already been accepted."
          : null

  const onSubmit = async (formData: SignUpFormData) => {
    try {
      await signUp({ token, userId, password: formData.password })
      setSignUpSucceeded(true)
    } catch {
      // captured by useMutation; surfaced via signUpError
    }
  }

  const deviceRedirect =
    deviceCodeInfo && Instant.parse(deviceCodeInfo.expiresAt).isAfter(Instant.now())
      ? generatePath(ROUTES.ADD_DEVICE, {
          deviceCode: deviceCodeInfo.deviceCode,
        }) + "?signUpSuccess=true"
      : null

  useEffect(() => {
    if (signUpSucceeded && deviceRedirect) {
      setDeviceCodeInfo(null)
    }
  }, [signUpSucceeded, deviceRedirect, setDeviceCodeInfo])

  if (!token || !userId) {
    return null
  }

  if (signUpSucceeded) {
    return <PostLoginRedirect redirect={deviceRedirect} />
  }

  if (isCheckingInvitation) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12" aria-label="Checking invitation">
          <Spinner className="h-8 w-8" />
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <HeroImage src={loginScreenImageUrl} alt="sign-up" />
      <h2 className="mb-8 px-6 text-center text-h2 font-bold">Create your log in password</h2>

      {hasValidInvitation && (
        <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-1">
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              disabled={isSigningUp}
              {...register("password")}
              placeholder="password"
              isError={!!errors.password?.message}
            />
            {errors.password?.message && (
              <p className="text-sm text-error">{errors.password.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="password-confirmation">Confirm password</Label>
            <PasswordInput
              id="password-confirmation"
              autoComplete="new-password"
              disabled={isSigningUp}
              {...register("passwordConfirmation")}
              placeholder="Confirm password"
              isError={!!errors.passwordConfirmation?.message}
            />
            {errors.passwordConfirmation?.message && (
              <p className="text-sm text-error">{errors.passwordConfirmation.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="agreeToTerms"
                  render={({ field }) => (
                    <Checkbox
                      id="agree-to-terms"
                      checked={field.value === true}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                  )}
                />
                <Label htmlFor="agree-to-terms" className="font-normal">
                  I agree to the{" "}
                  <button
                    type="button"
                    className="text-primary underline"
                    onClick={() => setIsTermsDrawerOpen(true)}
                  >
                    terms and conditions
                  </button>
                </Label>
              </div>
              {errors.agreeToTerms?.message && (
                <p className="text-sm text-error">{errors.agreeToTerms.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="agreeToTextMessages"
                  render={({ field }) => (
                    <Checkbox
                      id="agree-to-text-messages"
                      checked={field.value === true}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                  )}
                />
                <Label htmlFor="agree-to-text-messages" className="font-normal">
                  I agree to receive text messages
                </Label>
              </div>
              {errors.agreeToTextMessages?.message && (
                <p className="text-sm text-error">{errors.agreeToTextMessages.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1 pl-4 text-sm text-primary-contrast/70">
              <p>• We may use technology to automatically send SMS messages.</p>
              <p>
                • You may opt out at any time by replying STOP or contacting us; however, by doing
                so we will no longer be able to provide you access to Bloomlife Connects.
              </p>
              <p>• Standard message and data rates may apply.</p>
            </div>
          </div>

          {signUpErrorMessage && (
            <div className="rounded-md border border-error/30 bg-error/5 p-3">
              <p className="text-sm text-error">{signUpErrorMessage}</p>
            </div>
          )}

          <Button type="submit" disabled={isSigningUp} className="flex w-full gap-3 py-2 text-base">
            {isSigningUp && <Spinner />}
            Set password
          </Button>
        </form>
      )}

      <CopyRightsFooter />

      <Dialog open={invitationErrorMessage !== null}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>An error occurred</DialogTitle>
          </DialogHeader>
          <p className="mt-2 text-sm">{invitationErrorMessage}</p>
          <Button
            type="button"
            className="mt-4 w-full"
            onClick={() => navigate(ROUTES.SIGN_IN, { replace: true })}
          >
            Go back and log in
          </Button>
        </DialogContent>
      </Dialog>

      <TermsAndConditionsDrawer
        open={isTermsDrawerOpen}
        onClose={() => setIsTermsDrawerOpen(false)}
      />
    </Page>
  )
}

export default SignUpPage
